import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { style } from 'typestyle';
import { type CSSProperties } from 'typestyle/lib/types';
import { useTranslation } from 'react-i18next';
import CommonStyles from '../../core/common.styles';
import { IsMobileContext } from '../../core/context/is-mobile';
import * as tabStyles from './dashboard/statistics-tabs.styles';
import OverviewTab from './dashboard/tabs/overview-tab.component';
import OfficeDetailTab from './dashboard/tabs/office-detail-tab.component';
import ReportingTab from './dashboard/tabs/reporting-tab.component';
import DisplaysTab from './dashboard/tabs/displays-tab.component';
import DefaultThresholdsTab from './dashboard/tabs/default-thresholds-tab.component';
import { useKeycloak } from '../authentication/keycloak/KeycloakProvider';
import AppConstants from '../../core/app.constants';

type TabKey = 'overview' | 'office' | 'reporting' | 'displays' | 'thresholds';

const TAB_KEYS: TabKey[] = ['overview', 'office', 'reporting', 'displays', 'thresholds'];

const hiddenPanel: CSSProperties = { display: 'none' };

const StatisticsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const keycloak = useKeycloak();

  // The organization-wide default threshold (and the "Go to offices" CTA on that tab) is
  // admin-only on the backend, so office managers never get the tab — not even via ?tab=thresholds.
  const canManageDefaultThreshold =
    keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE) || keycloak.hasRealmRole(AppConstants.ADMIN_ROLE);
  const tabKeys = useMemo<TabKey[]>(
    () => (canManageDefaultThreshold ? TAB_KEYS : TAB_KEYS.filter((tabKey) => tabKey !== 'thresholds')),
    [canManageDefaultThreshold],
  );

  const activeTab = useMemo<TabKey>(() => {
    const requested = searchParams.get('tab');
    return tabKeys.includes(requested as TabKey) ? (requested as TabKey) : 'overview';
  }, [searchParams, tabKeys]);

  const selectedOfficeId = searchParams.get('officeId') ?? undefined;

  // A ?tab= the user may not open (e.g. an office manager following a ?tab=thresholds link) falls
  // back to Overview above — rewrite the URL to match, in place, so it doesn't keep lying about it.
  useEffect(() => {
    const requested = searchParams.get('tab');
    if (requested === null || requested === activeTab) {
      return;
    }

    const next: Record<string, string> = { tab: activeTab };
    if (selectedOfficeId) {
      next.officeId = selectedOfficeId;
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, activeTab, selectedOfficeId, setSearchParams]);

  // Tabs are lazy-mounted on first visit, then kept mounted (hidden via CSS) so switching back
  // to a tab doesn't lose already-fetched data or trigger a redundant refetch.
  const [visitedTabs, setVisitedTabs] = useState<Set<TabKey>>(() => new Set([activeTab]));
  useEffect(() => {
    setVisitedTabs((current) => (current.has(activeTab) ? current : new Set(current).add(activeTab)));
  }, [activeTab]);

  // Overview/Office detail stay mounted-but-hidden once visited (see visitedTabs above), so they
  // never know a default-threshold save elsewhere changed the numbers they already fetched.
  // Bumping this forces both to refetch next time they're shown.
  const [dashboardDataVersion, setDashboardDataVersion] = useState<number>(0);
  const bumpDashboardDataVersion = useCallback((): void => setDashboardDataVersion((version) => version + 1), []);

  const setTab = useCallback(
    (tab: TabKey, officeId?: string): void => {
      const next: Record<string, string> = { tab };
      // The selected office is shared context across Office detail/Reporting/Displays — preserve
      // it across every plain tab click, not just when navigating directly into Office detail,
      // so switching tabs and back doesn't silently drop the user's selection.
      if (officeId) {
        next.officeId = officeId;
      } else if (selectedOfficeId) {
        next.officeId = selectedOfficeId;
      }

      setSearchParams(next);
    },
    [selectedOfficeId, setSearchParams],
  );

  const goToOfficeDetail = useCallback(
    (officeId: string): void => {
      setTab('office', officeId);
    },
    [setTab],
  );

  return (
    <div className={style(CommonStyles.pageMargin)}>
      <div className={style(CommonStyles.bodyMargin(isMobile))}>
        <h1 className={style(tabStyles.pageTitle)}>{t('statistics')}</h1>
        <p className={style(tabStyles.pageSub)}>{t('dashboard-page-subtitle')}</p>

        <div className={style(tabStyles.tabBar(isMobile))}>
          {tabKeys.map((tabKey) => (
            <div
              key={tabKey}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === tabKey}
              className={style(tabStyles.tab(activeTab === tabKey))}
              onClick={(): void => setTab(tabKey)}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setTab(tabKey);
                }
              }}
            >
              {t(`dashboard-tab-${tabKey}`)}
            </div>
          ))}
        </div>

        {/* Each tab lazy-mounts on first visit (matching the pre-existing behavior — no wasted
            upfront fetches for tabs never opened) and then stays mounted, just hidden via
            display:none (the approved mockup's own tab-panel technique), so switching back to an
            already-visited tab doesn't lose its data or trigger a redundant refetch. */}
        {visitedTabs.has('overview') && (
          <div className={style(activeTab === 'overview' ? undefined : hiddenPanel)}>
            <OverviewTab onSelectOffice={goToOfficeDetail} dataVersion={dashboardDataVersion} />
          </div>
        )}
        {visitedTabs.has('office') && (
          <div className={style(activeTab === 'office' ? undefined : hiddenPanel)}>
            <OfficeDetailTab
              selectedOfficeId={selectedOfficeId}
              onSelectOffice={(officeId): void => setTab('office', officeId)}
              dataVersion={dashboardDataVersion}
            />
          </div>
        )}
        {visitedTabs.has('reporting') && (
          <div className={style(activeTab === 'reporting' ? undefined : hiddenPanel)}>
            <ReportingTab selectedOfficeId={selectedOfficeId} />
          </div>
        )}
        {visitedTabs.has('displays') && (
          <div className={style(activeTab === 'displays' ? undefined : hiddenPanel)}>
            <DisplaysTab selectedOfficeId={selectedOfficeId} />
          </div>
        )}
        {visitedTabs.has('thresholds') && (
          <div className={style(activeTab === 'thresholds' ? undefined : hiddenPanel)}>
            <DefaultThresholdsTab onSaved={bumpDashboardDataVersion} />
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsPage;
